import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  format, startOfWeek, endOfWeek, eachDayOfInterval, getWeek, parseISO,
} from 'date-fns';
import { Loader2, Printer, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { getContrastTextColor } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// --------- Helpers (identical math used by Approve Time view) -----------
const hoursToMinutes = (h) => (h == null ? null : Math.round(Number(h) * 60));
const formatMinutesToDisplay = (mins) => {
  const m = Math.max(0, Number(mins) || 0);
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h && r) return `${h}h ${r}m`;
  if (h) return `${h}h`;
  return `${r}m`;
};
// Lauren (owner, 2026-02-26): one of her accounting systems only
// consumes decimal hours (e.g. `37.28h`). She still wants h/m for
// day-to-day reading, so week totals now render BOTH.
// Kept as a separate helper so daily subtotals can adopt it later
// without a code archaeology dig.
const formatMinutesToDecimal = (mins) => {
  const m = Math.max(0, Number(mins) || 0);
  return `${(m / 60).toFixed(2)}h`;
};
const formatWeekTotal = (mins) => `${formatMinutesToDisplay(mins)} (${formatMinutesToDecimal(mins)})`;
const getEntryMinutes = (entry, field) => {
  if (!entry) return 0;
  const minutesField = `${field}_minutes`;
  const hoursField = `${field}_hours`;
  if (entry[minutesField] != null) return Number(entry[minutesField]) || 0;
  if (entry[hoursField] != null) return Math.round(Number(entry[hoursField]) * 60);
  return 0;
};

// Friendly Title Case for activity labels stored as raw UPPER_SNAKE_CASE
// (e.g. "END_OF_DAY_CLEANUP" -> "End of Day Cleanup"). Same logic as
// Timesheet.js so the print view matches the on-screen Day View.
const titleCaseLabel = (text) => {
  if (!text) return text;
  const s = String(text);
  if (!/^[A-Z0-9_\- ]+$/.test(s) || !/[_-]/.test(s)) return s;
  const small = new Set(['of', 'and', 'or', 'the', 'a', 'an', 'in', 'on', 'to', 'for', 'from']);
  return s
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .split(' ')
    .filter(Boolean)
    .map((w, i) => (i > 0 && small.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
};

// Known shop-activity labels (canonicalized for case-insensitive matching).
// Used as a third detection path in the shop-prep rollup so that an entry
// with title "End of Day Cleanup" is recognized as a shop activity even if
// its shop_category field is null AND its job_number doesn't start with
// SHOP-. This is the payroll safety net for manually-edited entries.
// Mirrors SHOP_TITLE_KEYS in Timesheet.js — keep them in sync.
const SHOP_TITLE_KEYS = new Set([
  'shopprep', 'training', 'errands', 'equipment',
  'endofdaycleanup', 'other',
  // lunch intentionally NOT included — unpaid and printed separately
]);

const isShopActivity = (e) => {
  if (!e) return false;
  if (e.shop_category) return true;
  const jn = String(e.job_number || '').toUpperCase();
  if (jn === 'SHOP' || jn.startsWith('SHOP-') || jn.startsWith('SHOP_') || jn.startsWith('SHOP ')) {
    return true;
  }
  // Legacy "Other" shop entries (job_number='OTHER' from pre-2026-06-08
  // desktop Add Entry) — still paid shop activity.
  if (jn === 'OTHER' || jn.startsWith('OTHER-') || jn.startsWith('OTHER ')) {
    return true;
  }
  const titleKey = String(e.job_title || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  return titleKey && SHOP_TITLE_KEYS.has(titleKey);
};

/**
 * Per-employee weekly timesheet PDF preview.
 *
 * Why this route exists: opening the print view via `window.open()` +
 * `document.write()` worked in Chrome but Safari blanked the document the
 * moment `window.print()` fired ("Untitled" white page, blank PDF). The
 * PreCon / Locate / Proposal previews never had this problem because they
 * are real React routes printed from the SAME tab — so we follow that
 * exact pattern here.
 *
 * Visual layout (calendar Sun→Sat with day cards + Invoice # box +
 * detailed Time Entries list below) is the SAME as what the user already
 * approved on the Approve Time page; only the delivery mechanism changed.
 */
export default function TimesheetMemberPreview() {
  const { userId, weekStart } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();

  // Feb-2026: Lauren (owner) reported that after reviewing a worker's
  // timesheet from the Team Timesheets tile list, hitting Back dropped
  // her onto her own personal timesheet and she had to reselect the
  // "Team Timesheets" tab every time. The old `navigate(-1)` behaved
  // inconsistently across browser-history vs new-tab launches.
  //
  // The preview can be opened from three contexts today:
  //   1. TimesheetApproval.js → `window.open(url + '?from=approval', '_blank')`
  //      → back to /timesheet/approval
  //   2. Timesheet.js "Team Timesheets" tab → same tab navigation with
  //      `?back_to_tab=team-timesheets`
  //      → back to /timesheet?tab=team-timesheets
  //   3. Anywhere else / bookmark / refresh
  //      → default to /timesheet?tab=team-timesheets which is what the
  //        approver almost always wants to be looking at anyway
  const handleBack = () => {
    const from = searchParams.get('from');
    const backToTab = searchParams.get('back_to_tab');
    if (from === 'approval') {
      navigate('/timesheet/approval');
      return;
    }
    if (backToTab === 'team-timesheets') {
      navigate('/timesheet?tab=team-timesheets');
      return;
    }
    navigate('/timesheet?tab=team-timesheets');
  };

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [entries, setEntries] = useState([]);
  // Map of initials → calendar_color so the bubbles in the calendar
  // match the avatar colors used elsewhere in the app (Layout header,
  // Calendar, Team page). Built once from /api/team and then keyed by
  // the comma-separated `team_initials` already stored on each
  // timesheet entry — we just look up each pair of initials and render
  // the bubble with the same hex color the rest of the UI uses.
  const [colorByInitials, setColorByInitials] = useState({});
  // Job-number badge toggle (off by default — payroll asked for a cleaner
  // list; numbers can be shown via the header checkbox and the preference
  // sticks in localStorage so it doesn't reset on every print).
  const [showJobNumbers, setShowJobNumbers] = useState(() => {
    try { return localStorage.getItem('tsmp_show_job_numbers') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('tsmp_show_job_numbers', showJobNumbers ? '1' : '0'); } catch {}
  }, [showJobNumbers]);

  // "Fit to one page" — when a week has a heavy day (5+ entries on one
  // column) the calendar block exceeds the printable height of a
  // landscape Letter page and Chrome splits the column across 2 pages.
  // Toggling this shrinks the calendar block via CSS `transform: scale()`
  // ONLY at print time so the screen UI stays full-size and crisp.
  // Persisted in localStorage so the user doesn't have to re-toggle on
  // every print.
  const [fitToPage, setFitToPage] = useState(() => {
    try { return localStorage.getItem('tsmp_fit_to_page') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('tsmp_fit_to_page', fitToPage ? '1' : '0'); } catch {}
  }, [fitToPage]);
  const invoiceNumber = searchParams.get('invoice') || '';

  // Parse the YYYY-MM-DD weekStart param into a true Sunday-anchored week.
  const start = startOfWeek(parseISO(weekStart), { weekStartsOn: 0 });
  const end = endOfWeek(start, { weekStartsOn: 0 });
  const weekDays = eachDayOfInterval({ start, end });

  // Inject the print stylesheet into <head> as a real <style> element.
  // Safari's "Save as PDF" path snapshots stylesheets at the moment print()
  // fires, and stylesheets that live inside the React tree (rendered as a
  // JSX <style> child) are occasionally missed in that snapshot — producing
  // a blank PDF even though the screen and print preview look fine.
  // Putting the rules in document.head guarantees Safari sees them.
  useEffect(() => {
    const el = document.createElement('style');
    el.setAttribute('data-tsm-print-styles', '1');
    el.textContent = TS_MEMBER_PREVIEW_STYLES;
    document.head.appendChild(el);
    return () => { el.remove(); };
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [userRes, entriesRes] = await Promise.all([
          axios.get(`${API}/team`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/timesheets`, {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              user_id: userId,
              start_date: format(start, 'yyyy-MM-dd'),
              end_date: format(end, 'yyyy-MM-dd'),
            },
          }),
        ]);
        const matched = (userRes.data || []).find((u) => u.id === userId);
        setUser(matched || { id: userId, full_name: 'Team Member' });
        // Build initials → calendar_color map. Initials are derived from
        // each member's full_name (first letter of first + last word) —
        // the same algorithm Layout.js / TimesheetWall.js use to render
        // member avatars, so the keys here line up cleanly with what's
        // stored in the timesheet entry's team_initials field.
        const colorMap = {};
        for (const u of (userRes.data || [])) {
          if (!u?.full_name || !u?.calendar_color) continue;
          const parts = u.full_name.trim().split(/\s+/);
          const ini = ((parts[0]?.[0] || '') + (parts[parts.length - 1]?.[0] || ''))
            .toUpperCase();
          if (ini) colorMap[ini] = u.calendar_color;
        }
        setColorByInitials(colorMap);
        setEntries(entriesRes.data || []);
      } catch (e) {
        console.error('Failed to load timesheet preview', e);
        toast.error('Failed to load timesheet');
      } finally {
        setLoading(false);
      }
    };
    if (userId && weekStart && token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, weekStart, token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-600">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Loading timesheet...</span>
        </div>
      </div>
    );
  }

  // ---- Build day-by-day data (Sun→Sat sorted) ---------------------------
  const days = weekDays.map((day) => {
    const dStr = format(day, 'yyyy-MM-dd');
    const dayEntries = (entries || [])
      .filter((e) => e.date === dStr)
      .sort((a, b) => {
        const aT = a.start_time || a.created_at || '';
        const bT = b.start_time || b.created_at || '';
        return aT.localeCompare(bT);
      });
    const dayTotal = dayEntries.reduce(
      (s, e) => s + (e.total_minutes ?? hoursToMinutes(e.total_hours) ?? 0),
      0,
    );
    return { day, dStr, dayEntries, dayTotal };
  });
  const weekTotal = days.reduce((s, d) => s + d.dayTotal, 0);
  const totalEntries = (entries || []).length;

  // ---- Entry card / row helpers (identical look to old PDF) -------------
  const entryBadge = (e) => {
    const jn = (e.job_number || '').toUpperCase();
    if (!e.job_number) return null;
    if (jn.startsWith('OTHER') || jn === 'OTHER') {
      return <span className="ribbon-other">OTHER</span>;
    }
    if (jn.startsWith('SHOP-') || jn === 'SHOP') {
      return <span className="ribbon-shop">SHOP</span>;
    }
    return <span className="ribbon-job">{e.job_number}</span>;
  };

  return (
    <div className={`ts-preview-root${fitToPage ? ' ts-fit-to-page' : ''}`}>
      {/* Sticky toolbar — hidden in print via .no-print */}
      <div className="no-print sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={handleBack}
              className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200 transition-all mr-2"
              data-testid="ts-preview-back"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <span className="font-semibold text-slate-700 text-sm">
              Timesheet Preview — {user?.full_name}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <label
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-50 text-slate-700 border border-slate-200 cursor-pointer select-none"
              data-testid="ts-preview-show-job-numbers-label"
            >
              <input
                type="checkbox"
                checked={showJobNumbers}
                onChange={(e) => setShowJobNumbers(e.target.checked)}
                className="accent-orange-600"
                data-testid="ts-preview-show-job-numbers-toggle"
              />
              Show Job Numbers
            </label>
            <label
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-50 text-slate-700 border border-slate-200 cursor-pointer select-none"
              title="Shrinks the calendar block on the printed page so a busy day doesn't split across 2 pages. Only affects the printout, not this screen."
              data-testid="ts-preview-fit-to-page-label"
            >
              <input
                type="checkbox"
                checked={fitToPage}
                onChange={(e) => setFitToPage(e.target.checked)}
                className="accent-orange-600"
                data-testid="ts-preview-fit-to-page-toggle"
              />
              Fit to 1 page
            </label>
            <button
              onClick={() => window.print()}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-orange-600 text-white hover:bg-orange-700 transition-all shadow-sm"
              data-testid="ts-preview-print"
            >
              <Printer className="w-4 h-4" />
              Print / Save PDF
            </button>
          </div>
        </div>
      </div>

      {/* Printable document */}
      <div className="ts-doc">
        {/* Brand strip */}
        <div className="brand">
          <img
            src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/69b5afabd540121a86879b5b/e29b4c6b2_CLLLogoTransparent.png"
            alt="Center Line Locating"
          />
          <div className="brand-info">
            <b>Center Line Locating</b><br />
            12030 Goodrich Dr, Charlotte, NC 28273 &nbsp;·&nbsp; (704) 315-5820 &nbsp;·&nbsp; requests@cllocating.com
          </div>
          <div className="brand-doc">
            <div className="lbl">Employee Timesheet</div>
            <div className="num">#TS-{getWeek(start)}-{format(start, 'yyyy')}</div>
          </div>
        </div>

        {/* Hero summary */}
        <div className="hero">
          <div className="hero-left">
            <div className="hero-week">Week {getWeek(start)} · {format(start, 'yyyy')}</div>
            <div className="hero-range">{format(start, 'MMM d')} – {format(end, 'MMM d, yyyy')}</div>
            <div className="employee-name">{user?.full_name || '—'}</div>
          </div>
          <div className="hero-right">
            <div className="total-num">{formatWeekTotal(weekTotal)}</div>
            <div className="total-lbl">Week Total</div>
          </div>
        </div>

        {/* Calendar block: 7-day grid only (mini-month + invoice box +
            duplicate WEEK TOTAL pill were removed 2026-05-26 — owner
            asked for a leaner layout focused on payroll review). */}
        <div className="cal-block">
          <div className="cal-grid">
            {days.map(({ day, dayEntries, dayTotal }) => {
              // Sum SHOP entries separately so we can print a "Shop Prep"
              // total at the end of the day (only when there was any
              // shop-time on that day). Detection uses the shared
              // isShopActivity() helper above — shop_category field,
              // SHOP-prefixed job_number, OR a known shop activity title
              // (the manual-edit safety net added 2026-06-08).
              const shopMinutes = dayEntries.reduce((s, e) => {
                if (!isShopActivity(e)) return s;
                return s + (e.total_minutes ?? hoursToMinutes(e.total_hours) ?? 0);
              }, 0);
              return (
              <div key={day.toISOString()} className={`cal-col${dayEntries.length === 0 && (day.getDay() === 0 || day.getDay() === 6) ? ' cal-col--collapsed' : ''}`}>
                <div className="cal-col-head">
                  <div>
                    <div className="cal-day-name">{format(day, 'EEE').toUpperCase()}</div>
                    <div className="cal-day-num">{format(day, 'd')}</div>
                  </div>
                  {dayTotal > 0 && (
                    <div className="cal-day-total">{formatMinutesToDisplay(dayTotal)}</div>
                  )}
                </div>
                <div className="cal-col-body">
                  {dayEntries.length === 0 ? (
                    <div className="cal-empty">No entries</div>
                  ) : dayEntries.map((e) => {
                    const title = titleCaseLabel(e.job_title || e.notes || '—');
                    const total = e.total_minutes ?? hoursToMinutes(e.total_hours) ?? 0;
                    const client = e.client_name || '';
                    // Contact name + team-initials pill come straight off
                    // the timesheet entry doc — already enriched server-side
                    // when the entry was created — so no extra job/team
                    // fetches are needed. SHOP entries have neither set
                    // and the conditional render hides them cleanly.
                    const contact = e.contact_name || '';
                    const teamInitials = e.team_initials || '';
                    return (
                      <div key={e.id} className="cal-card">
                        {showJobNumbers && entryBadge(e)}
                        <div className="cal-title" title={title}>{title}</div>
                        {client && <div className="cal-client">{client}</div>}
                        {contact && (
                          <div className="cal-contact">Contact: {contact}</div>
                        )}
                        {teamInitials && (
                          <div className="cal-team">
                            {String(teamInitials).split(/[,\s]+/).filter(Boolean).map((ini, i) => {
                              const color = colorByInitials[ini.toUpperCase()] || '#e2e8f0';
                              const isDefault = !colorByInitials[ini.toUpperCase()];
                              return (
                                <span
                                  key={`${ini}-${i}`}
                                  className="cal-team-bubble"
                                  title={ini}
                                  style={{
                                    backgroundColor: color,
                                    color: isDefault ? '#334155' : getContrastTextColor(color),
                                    borderColor: isDefault ? '#cbd5e1' : color,
                                  }}
                                >
                                  {ini}
                                </span>
                              );
                            })}
                          </div>
                        )}
                        <div className="cal-total">{formatMinutesToDisplay(total)}</div>
                        {/* Empty payroll review box — payroll writes by
                            hand on the printout. No label inside so the
                            box appears as a clean handwriting target. */}
                        <div className="cal-payroll-box" aria-label="Payroll review" />
                      </div>
                    );
                  })}
                  {/* Shop Prep daily roll-up — only rendered when the day
                      actually had Shop time. Mirrors the per-card pills
                      so payroll can see the day's total prep time at a
                      glance without manually tallying. */}
                  {shopMinutes > 0 && (
                    <div className="cal-shop-total" data-testid="cal-shop-total">
                      <span className="cal-shop-total-lbl">Shop Prep Total</span>
                      <span className="cal-shop-total-val">{formatMinutesToDisplay(shopMinutes)}</span>
                    </div>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </div>

        {/* Detailed Time Entries list grouped by day */}
        <div className="dt-wrap">
          <div className="dt-wrap-head">
            Time Entries <span className="dt-wrap-sub">· {totalEntries} entries · {formatWeekTotal(weekTotal)} total</span>
          </div>
          {days.filter((d) => d.dayEntries.length > 0).map(({ day, dayEntries, dayTotal }) => (
            <div key={day.toISOString()} className="dt-section">
              <div className="dt-section-head">
                <div className="dt-section-name">
                  <span className="dt-section-day">{format(day, 'EEEE').toUpperCase()}</span>
                  <span className="dt-section-date">{format(day, 'MMM d')}</span>
                  <span className="dt-section-count">{dayEntries.length} {dayEntries.length === 1 ? 'entry' : 'entries'}</span>
                </div>
                <div className="dt-section-total">{formatMinutesToDisplay(dayTotal)}</div>
              </div>
              <table className="dt-table">
                <tbody>
                  {dayEntries.map((e) => {
                    const title = titleCaseLabel(e.job_title || e.notes || '—');
                    const travel = getEntryMinutes(e, 'travel_to_job');
                    const onJob = getEntryMinutes(e, 'time_on_job');
                    const ret = getEntryMinutes(e, 'travel_from_job');
                    const total = e.total_minutes ?? hoursToMinutes(e.total_hours) ?? (travel + onJob + ret);
                    const crew = Array.isArray(e.crew_initials) && e.crew_initials.length
                      ? e.crew_initials.join(', ') : '';
                    return (
                      <tr key={e.id}>
                        {showJobNumbers && <td className="dt-badge">{entryBadge(e)}</td>}
                        <td className="dt-title">
                          <div className="dt-title-main">{title}</div>
                          {e.client_name && <div className="dt-title-sub">{e.client_name}</div>}
                        </td>
                        <td className="dt-crew">{crew && <span className="crew">{crew}</span>}</td>
                        <td className="dt-chips">
                          {travel > 0 && <span className="chip chip-travel">→ {formatMinutesToDisplay(travel)}</span>}
                          {onJob > 0 && <span className="chip chip-work">{formatMinutesToDisplay(onJob)}</span>}
                          {ret > 0 && <span className="chip chip-return">← {formatMinutesToDisplay(ret)}</span>}
                        </td>
                        <td className="dt-total">{formatMinutesToDisplay(total)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>

        <div className="week-bar">
          <span className="lbl">Week Total</span>
          <span className="val">{formatWeekTotal(weekTotal)}</span>
        </div>
        <div className="footer">
          Generated from CenterLine Locating FieldReady on {format(new Date(), 'MMMM d, yyyy h:mm a')}
        </div>
      </div>
    </div>
  );
}

// CSS for TimesheetMemberPreview, injected into <head> via useEffect.
// Why a constant + useEffect instead of an inline <style> in JSX?
//   - Safari's "Save as PDF" can produce a blank PDF when print rules
//     (especially @page + @media print) live in a JSX-rendered <style>.
//     Moving them to a real <style> element in document.head fixes it.
// Why `letter landscape` instead of `11in 8.5in`?
//   - Safari is more reliable with the named-keyword size syntax than
//     explicit dimensions; Chrome/Edge accept both.
const TS_MEMBER_PREVIEW_STYLES = `
        .ts-preview-root { min-height: 100vh; background:#f1f5f9; }
        .ts-doc { font-family:'Segoe UI', Arial, sans-serif; padding:18px; max-width:1100px; margin:18px auto; background:#fff; color:#1e293b; font-size:11px; border-radius:8px; box-shadow:0 2px 10px rgba(0,0,0,0.04); }
        .brand { display:flex; justify-content:space-between; align-items:center; padding-bottom:12px; border-bottom:1px solid #e2e8f0; margin-bottom:16px; }
        .brand img { height:50px; }
        .brand-info { text-align:center; font-size:10px; color:#64748b; line-height:1.4; flex:1; padding:0 12px; }
        .brand-info b { color:#334155; font-weight:600; font-size:11px; }
        .brand-doc { text-align:right; }
        .brand-doc .lbl { font-size:9px; color:#94a3b8; text-transform:uppercase; letter-spacing:1px; font-weight:600; }
        .brand-doc .num { font-size:12px; font-weight:700; color:#1e293b; margin-top:2px; }
        .hero { background:#fff; border-left:4px solid #f59e0b; border-radius:6px; padding:14px 18px; display:flex; justify-content:space-between; align-items:center; box-shadow:0 1px 3px rgba(0,0,0,0.04); border:1px solid #e2e8f0; margin-bottom:16px; }
        .hero-left .hero-week { font-size:10px; color:#64748b; text-transform:uppercase; letter-spacing:1.5px; font-weight:600; }
        .hero-left .hero-range { font-size:20px; font-weight:700; color:#1e293b; margin:4px 0 6px; }
        .hero-left .hero-meta { display:flex; align-items:center; gap:10px; font-size:11px; color:#64748b; }
        .hero-right { text-align:right; }
        .hero-right .total-num { font-size:32px; font-weight:800; color:#0f172a; line-height:1; }
        .hero-right .total-lbl { font-size:9px; color:#64748b; text-transform:uppercase; letter-spacing:1.5px; font-weight:600; margin-top:4px; }
        .employee-name { font-size:13px; color:#0f172a; font-weight:600; margin-top:4px; }
        .invoice-box { display:flex; align-items:stretch; border:2px solid #0f172a; border-radius:6px; overflow:hidden; margin-bottom:18px; }
        .invoice-box .invoice-label { background:#0f172a; color:#f59e0b; padding:12px 18px; font-size:11px; font-weight:700; letter-spacing:1.5px; text-transform:uppercase; display:flex; align-items:center; }
        .invoice-box .invoice-field { flex:1; padding:12px 16px; font-size:16px; color:#1e293b; font-family:'Courier New', monospace; }
        .cal-block { margin-bottom:20px; }
        /* Thin divider line between the header/hero and the 7-day grid —
           gives the print version a visual break similar to the
           Calendar's week-print layout the user referenced. */
        .cal-block::before { content:""; display:block; height:1px; background:#e2e8f0; margin:0 0 12px; }
        .cal-toolbar { display:flex; justify-content:space-between; align-items:flex-start; gap:18px; margin-bottom:10px; padding:10px 14px; background:#f8fafc; border:1px solid #e2e8f0; border-radius:8px; }
        .mm-wrap { width:200px; }
        .mm-title { font-size:11px; font-weight:700; color:#0f172a; margin-bottom:4px; text-transform:uppercase; letter-spacing:1px; }
        .mm-head { display:grid; grid-template-columns:repeat(7,1fr); gap:2px; margin-bottom:2px; }
        .mm-head span { text-align:center; font-size:8px; font-weight:700; color:#94a3b8; letter-spacing:0.5px; }
        .mm-grid { display:grid; grid-template-columns:repeat(7,1fr); gap:2px; }
        .mm-cell { text-align:center; font-size:9px; padding:2px 0; color:#475569; border-radius:3px; }
        .mm-cell.mm-other-month { color:#cbd5e1; }
        .mm-cell.mm-in-week { background:#fef3c7; color:#b45309; font-weight:700; }
        .cal-week-pill { background:#fef3c7; border-radius:8px; padding:8px 18px; text-align:right; min-width:140px; }
        .cal-week-pill-val { font-size:24px; font-weight:800; color:#b45309; line-height:1; }
        .cal-week-pill-lbl { font-size:9px; font-weight:700; color:#b45309; letter-spacing:1.5px; text-transform:uppercase; margin-top:3px; }
        /* Empty Sat / Sun columns collapse to a thin sliver — just the
           day name + number. Saves horizontal real-estate on busy weeks
           so the populated weekdays get wider cards. Only fires for
           weekends; weekday empties stay full width so the visual rhythm
           of the work week is preserved. Uses flex (not grid) so the
           collapsed weekends take a fixed 38px and the 5 weekdays share
           the remaining width equally — grid can't size individual
           columns by class. */
        .cal-grid { display:flex; gap:6px; align-items:stretch; }
        .cal-col { flex:1 1 0; min-width:0; border:1px solid #e2e8f0; border-radius:8px; background:#fff; min-height:200px; display:flex; flex-direction:column; overflow:hidden; box-sizing:border-box; page-break-inside:avoid; break-inside:avoid; }
        .cal-col--collapsed { flex:0 0 58px; background:#f8fafc; }
        .cal-col--collapsed .cal-col-body { display:none; }
        .cal-col-head { display:flex; justify-content:space-between; align-items:flex-start; padding:10px 12px; border-bottom:1px solid #f1f5f9; background:#fafbfc; border-radius:8px 8px 0 0; }
        .cal-day-name { font-size:10px; font-weight:700; color:#94a3b8; letter-spacing:1.5px; }
        .cal-day-num { font-size:28px; font-weight:800; color:#0f172a; line-height:1; margin-top:2px; }
        .cal-day-total { font-size:13px; font-weight:700; color:#0f172a; text-align:right; padding-top:4px; }
        .cal-col-body { padding:10px; display:flex; flex-direction:column; gap:8px; flex:1; }
        .cal-empty { font-size:10px; color:#cbd5e1; font-style:italic; text-align:center; margin-top:24px; }
        .cal-card { background:#f8fafc; border:1px solid #e2e8f0; border-radius:6px; padding:8px 10px; font-size:11px; line-height:1.4; box-sizing:border-box; page-break-inside:avoid; break-inside:avoid; }
        .cal-title { font-weight:600; color:#0f172a; margin-top:5px; font-size:11.5px; word-break:break-word; overflow-wrap:anywhere; }
        .cal-client { color:#64748b; font-size:10px; margin-top:2px; word-break:break-word; overflow-wrap:anywhere; }
        .cal-contact { color:#475569; font-size:10px; margin-top:2px; font-style:italic; word-break:break-word; overflow-wrap:anywhere; }
        .cal-team { display:flex; flex-wrap:wrap; gap:3px; margin-top:5px; }
        .cal-team-pill { display:inline-block; background:#e0f2fe; color:#075985; padding:1px 6px; border-radius:10px; font-size:9px; font-weight:600; border:1px solid #bae6fd; }
        /* Match the Calendar/Header avatar size (24px circle) so the
           bubbles look like the same component across the app. Colors
           come inline from the member's calendar_color. */
        .cal-team-bubble { display:inline-flex; align-items:center; justify-content:center; width:24px; height:24px; border-radius:50%; font-size:10px; font-weight:700; font-family:'Inconsolata',monospace; border:1px solid; box-shadow:0 1px 2px rgba(0,0,0,0.06); }
        .cal-total { color:#0f172a; font-weight:700; font-size:12px; margin-top:6px; }
        /* Empty handwriting box for payroll — no label, no overflow.
           box-sizing:border-box keeps the dashed border INSIDE the card
           so the box never sticks out the right edge like it did pre-
           2026-05-26. */
        .cal-payroll-box { margin-top:6px; min-height:30px; width:100%; border:1px dashed #cbd5e1; border-radius:4px; background:#ffffff; box-sizing:border-box; }
        .cal-shop-total { margin-top:auto; padding:6px 8px; background:#fff7ed; border:1px solid #fed7aa; border-radius:6px; display:flex; justify-content:space-between; align-items:center; }
        .cal-shop-total-lbl { font-size:9px; font-weight:700; color:#9a3412; letter-spacing:0.8px; text-transform:uppercase; }
        .cal-shop-total-val { font-size:11px; font-weight:700; color:#9a3412; }
        .ribbon-job { display:inline-block; background:#fef3c7; color:#b45309; padding:2px 6px; border-radius:4px; font-size:9px; font-weight:700; font-family:'Courier New', monospace; }
        .ribbon-shop, .ribbon-other { display:inline-block; background:#fff7ed; color:#ea580c; padding:2px 6px; border-radius:4px; font-size:9px; font-weight:700; letter-spacing:0.5px; }
        .dt-wrap { margin-top:14px; }
        .dt-wrap-head { font-size:14px; font-weight:700; color:#0f172a; padding:8px 0; }
        .dt-wrap-sub { font-size:10px; color:#64748b; font-weight:400; }
        .dt-section { margin-bottom:14px; border:1px solid #e2e8f0; border-radius:6px; overflow:hidden; page-break-inside:avoid; }
        .dt-section-head { display:flex; justify-content:space-between; align-items:center; padding:8px 14px; background:#f8fafc; border-bottom:1px solid #e2e8f0; }
        .dt-section-day { font-size:10px; font-weight:700; color:#475569; letter-spacing:1px; text-transform:uppercase; }
        .dt-section-date { font-size:11px; font-weight:600; color:#0f172a; margin-left:6px; }
        .dt-section-count { font-size:9px; color:#94a3b8; margin-left:8px; }
        .dt-section-total { font-size:13px; font-weight:700; color:#0f172a; }
        .dt-table { width:100%; border-collapse:collapse; font-size:11px; }
        .dt-table td { padding:6px 10px; border-top:1px solid #f1f5f9; vertical-align:middle; }
        .dt-table tr:first-child td { border-top:none; }
        .dt-badge { width:90px; }
        .dt-title-main { font-weight:600; color:#0f172a; }
        .dt-title-sub { font-size:9.5px; color:#64748b; margin-top:2px; }
        .dt-crew { width:90px; font-size:9px; color:#64748b; font-weight:600; }
        .dt-chips { width:240px; }
        .chip { display:inline-block; padding:1px 6px; border-radius:3px; font-size:9px; font-weight:600; margin-right:3px; }
        .chip-travel, .chip-return { background:#dbeafe; color:#1e40af; }
        .chip-work { background:#d1fae5; color:#065f46; }
        .dt-total { width:70px; text-align:right; font-weight:700; color:#0f172a; font-size:12px; }
        .week-bar { margin-top:8px; padding:12px 18px; background:#0f172a; color:#fff; border-radius:6px; display:flex; justify-content:space-between; align-items:center; }
        .week-bar .lbl { font-size:11px; text-transform:uppercase; letter-spacing:1.5px; color:#94a3b8; font-weight:600; }
        .week-bar .val { font-size:22px; font-weight:800; color:#f59e0b; }
        .footer { text-align:center; color:#94a3b8; font-size:9px; margin-top:18px; padding-top:10px; border-top:1px solid #e2e8f0; }

        /* Print rules — same proven pattern, but registered in <head>
           (see TS_MEMBER_PREVIEW_STYLES comment for the Safari rationale).
           Additions 2026-05-26: keep the 7-day grid on one page (it used
           to split mid-week across 2 pages when scaled to portrait); push
           the Time Entries detail to its own page so it doesn't crowd the
           calendar. */
        @media print {
          .no-print { display:none !important; }
          html, body { background:#fff !important; }
          .ts-preview-root { background:#fff !important; }
          .ts-doc { box-shadow:none !important; max-width:none !important; margin:0 !important; padding:0 !important; border-radius:0 !important; }
          .ts-doc, .ts-doc * { -webkit-print-color-adjust:exact !important; print-color-adjust:exact !important; color-adjust:exact !important; }
          .cal-grid { page-break-inside:avoid; break-inside:avoid; }
          .cal-col, .cal-card { page-break-inside:avoid; break-inside:avoid; }
          .dt-wrap { page-break-before:always; break-before:page; }
          /* "Fit to 1 page" toggle — when enabled, shrink the calendar
             block enough that a Wed with 5 entries still fits on one
             landscape Letter page. 0.78 was the largest scale that
             worked for Colten's heaviest week without cropping. Scale
             from top-left and widen by 1/scale so the block still fills
             the page width after the transform. Only the calendar
             block scales; the Time Entries list keeps full-size and
             flows naturally onto page 2+. */
          .ts-fit-to-page .cal-block {
            transform: scale(0.78);
            transform-origin: top left;
            width: 128.2%;
          }
        }
        /* Letter landscape — wider columns so cards stop wrapping and the
           full 7-day week fits on one continuous page. The user printed
           in portrait before this and had the week split across 2 pages. */
        @page { size: letter landscape; margin: 0.4in; }
`;
